import axios from 'axios';

// Utility function to clear tokens and redirect to login
const clearTokensAndRedirect = () => {
  localStorage.removeItem('access');
  localStorage.removeItem('refresh');
  localStorage.removeItem('tokenExpiry');
  
  // Only redirect if we're not already on the login page
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api/';

const API = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for adding token and checking expiry
API.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('access');
    const tokenExpiry = localStorage.getItem('tokenExpiry');
    
    if (token) {
      // Check if token is about to expire (within 5 minutes)
      if (tokenExpiry && new Date().getTime() > parseInt(tokenExpiry) - (5 * 60 * 1000)) {
        try {
          const refreshToken = localStorage.getItem('refresh');
          if (refreshToken) {
            const refreshResponse = await axios.post(`${BASE_URL}token/refresh/`, {
              refresh: refreshToken
            });
            
            if (refreshResponse.data.access) {
              localStorage.setItem('access', refreshResponse.data.access);
              const newExpiry = new Date().getTime() + (30 * 60 * 1000);
              localStorage.setItem('tokenExpiry', newExpiry);
              config.headers.Authorization = `Bearer ${refreshResponse.data.access}`;
              return config;
            }
          }
        } catch (error) {
          console.error('Proactive token refresh failed:', error);
          // Continue with current token, let the response interceptor handle 401
        }
      }
      
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token refresh
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest._isRefreshRequest) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Create a separate axios instance for token refresh to avoid circular dependency
        const refreshResponse = await axios.post(`${BASE_URL}token/refresh/`, {
          refresh: refreshToken
        });

        if (refreshResponse.data.access) {
          localStorage.setItem('access', refreshResponse.data.access);
          API.defaults.headers.common['Authorization'] = `Bearer ${refreshResponse.data.access}`;
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.access}`;
          
          // Update token expiry
          const expiryTime = new Date().getTime() + (30 * 60 * 1000); // 30 minutes
          localStorage.setItem('tokenExpiry', expiryTime);

          return API(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        clearTokensAndRedirect();
      }
    }

    return Promise.reject(error);
  }
);

export default API;